import React from "react";
import { Card, Stack, Text, Box, Shelf } from "@chaser/components";
import { FileText, Users } from "lucide-react";
import pluralize from "pluralize";
import BuyLockPill from "../../../../../components/BuyLockPill";
import { ORGANISATION_MODULE_ID } from "../constants";
import useOrganisation from "../../../../../hooks/useOrganisation";

import styles from "../Addons.module.scss";
import { Integrations, Modules } from "../../../../../util/constants";
import { isExpiredTrialModule } from "../useAddonsState.helpers";

const toPeriodSuffix = (period) => (period === "year" ? " /yr" : " /mo");

const ModuleSummaryCard = ({
  moduleTotalMinor,
  moduleTotalAnnualizedMinor,
  formatCurrency,
  currencyCode,
  activeModuleCount,
  activeOrganisationAddonCount,
  moduleSubscriptions,
  availableModules,
  isOrganisationAddonsSelected,
  onSelectOrganisationAddons,
  selectedModule,
  selectedModuleMinor,
  onSelectModule,
  onSelectLockedModule,
}) => {
  const org = useOrganisation(null, { suspense: false });
  const isCashflowSupported =
    org?.accountingSoftware === Integrations.XERO ||
    org?.accountingSoftware === Integrations.ACCOUNTSIQ;
  const visibleModuleSubscriptions = (moduleSubscriptions || []).filter(
    (module) => module?.id !== ORGANISATION_MODULE_ID,
  );

  const activeModuleIds = new Set(
    visibleModuleSubscriptions.map((module) => module.id),
  );
  const activePeriods = Array.from(
    new Set(
      visibleModuleSubscriptions
        .filter((module) => module?.isActive)
        .map((module) => (module?.period === "year" ? "year" : "month")),
    ),
  );
  const hasMixedBillingPeriods = activePeriods.length > 1;
  const resolvedTotalPeriod = activePeriods[0] || "month";
  const totalLabel = "TOTAL SUBSCRIPTION";
  const totalSuffix = hasMixedBillingPeriods
    ? ""
    : toPeriodSuffix(resolvedTotalPeriod);
  const displayTotalMinor =
    !hasMixedBillingPeriods &&
    resolvedTotalPeriod === "year" &&
    Number.isFinite(moduleTotalAnnualizedMinor)
      ? moduleTotalAnnualizedMinor
      : moduleTotalMinor;

  const availableLockedModules = (availableModules || []).filter(
    (module) =>
      module?.id !== ORGANISATION_MODULE_ID && !activeModuleIds.has(module.id),
  );

  const visibleLockedModules = availableLockedModules.filter(
    (module) => module.id !== Modules.CASHFLOW || isCashflowSupported,
  );

  return (
    <Card p="medium">
      <Stack gap="small">
        <Text fontSize="12px" color="Mid Neutral 1">
          {totalLabel}
        </Text>
        <Text fontSize="heading.2" fontWeight="bold">
          {formatCurrency(displayTotalMinor / 100, currencyCode)}
          {totalSuffix && (
            <Text fontSize="small" color="Mid Neutral 1">
              {" "}
              {totalSuffix}
            </Text>
          )}
        </Text>
        <Text color="Mid Neutral 1" className={styles.activeCount}>
          {activeModuleCount} active {pluralize("module", activeModuleCount)}
        </Text>
        <Text color="Mid Neutral 1" className={styles.activeCount}>
          {activeOrganisationAddonCount} active organisation add-ons
        </Text>

        <div className={styles.railDivider} />

        <Text fontSize="12px" color="Mid Neutral 1">
          ORGANISATION ADD-ONS
        </Text>

        <Box
          className={`${
            isOrganisationAddonsSelected
              ? styles.moduleItemActive
              : styles.moduleItemMuted
          } ${styles.moduleItemInteractive}`}
        >
          <Shelf
            alignItems="center"
            justifyContent="space-between"
            onClick={onSelectOrganisationAddons}
          >
            <Shelf
              alignItems="center"
              gap="small"
              className={styles.moduleItemLeft}
            >
              <Box
                className={
                  isOrganisationAddonsSelected
                    ? styles.moduleIconActive
                    : styles.moduleIconMuted
                }
              >
                <Users
                  size={14}
                  color={isOrganisationAddonsSelected ? "#E87750" : "#7C8A9C"}
                  strokeWidth={2}
                />
              </Box>
              <Stack gap="xsmall">
                <Text>All organisation add-ons</Text>
                <Text color="Mid Neutral 1" className={styles.modulePlanName}>
                  Organisation add-ons configured
                </Text>
              </Stack>
            </Shelf>
          </Shelf>
        </Box>

        <div className={styles.railDivider} />

        <Text fontSize="12px" color="Mid Neutral 1">
          MODULES
        </Text>

        {visibleModuleSubscriptions.length > 0 &&
          visibleModuleSubscriptions.map((module) => {
            const isExpiredTrial = isExpiredTrialModule(module);
            const isSelected =
              !isOrganisationAddonsSelected && module.id === selectedModule?.id;

            const modulePlanLabel = isExpiredTrial
              ? "Trial expired"
              : module?.isInTrial
                ? "Trial"
                : module?.planName || "No plan";

            return (
              <Box
                key={module.id}
                className={`${
                  isSelected ? styles.moduleItemActive : styles.moduleItemMuted
                } ${styles.moduleItemInteractive}`}
              >
                <Shelf
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => onSelectModule(module.id)}
                >
                  <Shelf
                    alignItems="center"
                    gap="small"
                    className={styles.moduleItemLeft}
                  >
                    <Box
                      className={
                        isSelected
                          ? styles.moduleIconActive
                          : styles.moduleIconMuted
                      }
                    >
                      <FileText
                        size={14}
                        color={isSelected ? "#E87750" : "#7C8A9C"}
                        strokeWidth={2}
                      />
                    </Box>
                    <Stack gap="xsmall">
                      <Shelf alignItems="center" gap="xsmall">
                        <Text>{module.moduleName}</Text>
                        {isExpiredTrial && (
                          <span className={styles.moduleTrialBadge}>
                            EXPIRED
                          </span>
                        )}
                        {module?.isInTrial && !isExpiredTrial && (
                          <span className={styles.moduleTrialBadge}>
                            IN TRIAL
                          </span>
                        )}
                      </Shelf>
                      {modulePlanLabel ? (
                        <Text
                          color="Mid Neutral 1"
                          className={styles.modulePlanName}
                        >
                          {modulePlanLabel}
                        </Text>
                      ) : null}
                    </Stack>
                  </Shelf>
                  {module?.isInTrial ? null : (
                    <Text className={styles.modulePriceInline}>
                      {formatCurrency(
                        (module.id === selectedModule?.id
                          ? selectedModuleMinor
                          : module.amountMinor || 0) / 100,
                        currencyCode,
                      )}
                      <Text fontSize="small" color="Mid Neutral 1">
                        {" "}
                        {toPeriodSuffix(module?.period)}
                      </Text>
                    </Text>
                  )}
                </Shelf>
              </Box>
            );
          })}

        {visibleLockedModules.length > 0 && (
          <>
            <Text fontSize="12px" color="Mid Neutral 1">
              ADD A MODULE
            </Text>
            {visibleLockedModules.map((module) => {
              const isSelected =
                !isOrganisationAddonsSelected &&
                module.id === selectedModule?.id;

              const isExpiredTrial = isExpiredTrialModule(module);
              const lockedModulePlanLabel = isExpiredTrial
                ? "Trial expired"
                : module?.isInTrial
                  ? "Trial"
                  : module?.planName || "No plan";

              return (
                <Box
                  key={module.id}
                  className={`${
                    isSelected
                      ? styles.moduleItemActive
                      : styles.moduleItemMuted
                  } ${styles.moduleItemLocked}`}
                >
                  <Shelf
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => onSelectLockedModule?.(module.id)}
                  >
                    <Shelf
                      alignItems="center"
                      gap="small"
                      className={styles.moduleItemLeft}
                    >
                      <Box className={styles.moduleIconMuted}>
                        <FileText size={14} color="#9AA8B5" strokeWidth={2} />
                      </Box>
                      <Stack gap="xsmall">
                        <Text color={isSelected ? undefined : "Mid Neutral 1"}>
                          {module.moduleName || "Module"}
                        </Text>
                        {lockedModulePlanLabel ? (
                          <Text
                            color="Mid Neutral 1"
                            className={styles.modulePlanName}
                          >
                            {lockedModulePlanLabel}
                          </Text>
                        ) : null}
                      </Stack>
                    </Shelf>
                    <BuyLockPill />
                  </Shelf>
                </Box>
              );
            })}
          </>
        )}
      </Stack>
    </Card>
  );
};

export default ModuleSummaryCard;
